import { Component, inject, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationPopupComponent } from '../../components/confirmation-popup/confirmation-popup.component';
import { ListActionButtonsComponent } from '../../components/list-action-buttons/list-action-buttons.component';
import { ModalComponent } from '../../components/modal/modal.component';
import { CountryService } from '../../services/country.service';
import { GlobalService } from '../../services/global.service';
import { CommonModule } from '@angular/common';
import { AgGridListComponent } from '../../components/ag-grid-list/ag-grid-list.component';
import { BulkUploadComponent } from '../../components/bulk-upload/bulk-upload.component';
import { ButtonComponent } from '../../components/button/button.component';
import { HeaderComponent } from '../../components/header/header.component';
import { SearchInputComponent } from '../../components/search-input/search-input.component';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { TextInputComponent } from '../../components/text-input/text-input.component';
import { PluralPipe } from '../../pipes/plural.pipe';
import { DropdownService } from '../../services/dropdown.service';
import { BarcodeService } from '../../services/barcode.service';
import { ValidationService } from '../../services/validation.service';
import { FormService } from '../../services/form.service';
import { DropdownUploadButtonComponent } from '../../components/dropdown-upload-button/dropdown-upload-button.component';
import { FilterByKeyAndValuePipe } from '../../pipes/filter-by-key-and-value.pipe';

@Component({
  selector: 'app-barcode-mapping',
  imports: [HeaderComponent, ButtonComponent, CommonModule, FormsModule, ConfirmationPopupComponent, ModalComponent, TextInputComponent, ReactiveFormsModule, PluralPipe, AgGridListComponent, SearchInputComponent, BulkUploadComponent, SelectInputComponent, DropdownUploadButtonComponent, FilterByKeyAndValuePipe],
  templateUrl: './barcode-mapping.component.html',
  styleUrl: './barcode-mapping.component.css'
})
export class BarcodeMappingComponent {
  @ViewChild('confirmation_delete_modal') confirmation_delete_modal: ConfirmationPopupComponent | undefined;
  @ViewChild('confirmation_disabled_modal') confirmation_disabled_modal: ConfirmationPopupComponent | undefined;
  @ViewChild('form_modal') form_modal: ModalComponent | undefined;
  @ViewChild('view_modal') view_modal: ModalComponent | undefined;
  @ViewChild('bulk_modal') bulk_modal: any

  page_name = 'Barcode Mapping';
  form: FormGroup;
  bulk_form: FormGroup;
  params: any = {};
  selected_item: any = {}
  items: any = [];
  fs = inject(FormService);
  show_menu = false;

  // ── List filter dropdowns ──────────────────────────────────────────────────
  filter_academic_years: any[] = [];
  filter_exams: any[] = [];
  filter_subjects: any[] = [];
  filterDropdownRegistry: { [depended: number]: any } = {};
  cascading_filter_reset = false;

  // ── Modal dropdowns ────────────────────────────────────────────────────────
  modal_filter_academic_years: any[] = [];
  modal_filter_exams: any[] = [];
  modal_filter_subjects: any[] = [];
  filteredStudentList: any[] = [];
  modalDropdownRegistry: { [depended: number]: any } = {};

  registerFilterDropdown(event: { depended: number; ref: any }) {
    this.filterDropdownRegistry[event.depended] = event.ref;
  }

  onFilterDropdownChange(event: { value: any, depended: number }) {
    if (event.depended == 1) {
      this.params.university_id = event.value?.value || null;
      this.params.academic_year_id = null;
      this.params.exam_id = null;
      this.params.subject_id = null;
    }
    if (event.depended == 2) {
      this.params.academic_year_id = event.value?.value || null;
      this.params.exam_id = null;
      this.params.subject_id = null;
    }
    if (event.depended == 3) {
      this.params.exam_id = event.value?.value || null;
      this.params.subject_id = null;
    }
    if (event.depended == 4) {
      this.params.subject_id = event.value?.value || null;
    }
    this.update_list_filtered_options();
    // Resetting a dropdown makes it emit valueChange, which re-enters this handler for every
    // level below the one the user touched. Let those nested calls clear their params and skip
    // the fetch, so one university change results in a single list reload.
    const nested = this.cascading_filter_reset;
    this.cascading_filter_reset = true;
    for (const key of Object.keys(this.filterDropdownRegistry).map(Number)) {
      if (key > event.depended) this.filterDropdownRegistry[key]?.reset();
    }
    this.cascading_filter_reset = nested;
    if (!nested) this.get_list(this.params);
  }

  // Runs after apply_access_scope_to_params(): a locked filter is never overwritten, and the
  // "latest" row is picked from within the user's scope only, so the pre-patch can't land on a
  // university/subject the user isn't allowed to see.
  patch_latest_assessment() {
    const all: any[] = this.ds.dropdown_list?.assessment_list || [];
    if (!all.length) return;
    const locked = this.gs.access_scope.locked;
    const in_scope = all.filter((a: any) =>
      (!locked.university || a.university_id == this.params.university_id) &&
      (!locked.subject || a.subject_id == this.params.subject_id)
    );
    if (!in_scope.length) return;
    const latest = in_scope.reduce((prev: any, curr: any) => (curr.id > prev.id ? curr : prev));
    if (!locked.university) this.params.university_id = latest.university_id || null;
    this.params.academic_year_id = latest.academic_year_id || null;
    this.params.exam_id = (latest.exam_id && latest.exam_id !== 'none') ? latest.exam_id : null;
    if (!locked.subject) this.params.subject_id = latest.subject_id || null;
  }

  update_list_filtered_options() {
    const list = this.ds.dropdown_list;
    const data = list?.assessment_data_list;
    const has_uni = !!this.params.university_id;
    const has_ay = !!this.params.academic_year_id;
    const has_exam = !!this.params.exam_id;

    if (!has_uni || !data) {
      this.filter_academic_years = [];
    } else {
      const ids = [...new Set(data.filter((a: any) => a.university_id == this.params.university_id).map((a: any) => a.academic_year_id))];
      this.filter_academic_years = list?.academic_list?.filter((a: any) => ids.includes(a.id)) || [];
    }

    if (!has_uni || !has_ay || !data) {
      this.filter_exams = [];
    } else {
      const ids = [...new Set(data.filter((a: any) => a.university_id == this.params.university_id && a.academic_year_id == this.params.academic_year_id).map((a: any) => a.exam_id))];
      this.filter_exams = (list?.exam_list?.filter((e: any) => ids.includes(e.id)) || []).map((e: any) => ({ title: `${e.name}${e.code ? ' (' + e.code + ')' : ''}`, value: e.id }));
    }

    if (!has_uni || !has_ay || !has_exam || !data) {
      this.filter_subjects = [];
    } else {
      const seen = new Set();
      this.filter_subjects = data
        .filter((a: any) => a.university_id == this.params.university_id && a.academic_year_id == this.params.academic_year_id && a.exam_id == this.params.exam_id)
        .reduce((acc: any[], a: any) => {
          const sid = a.subject?.id || a.subject_id;
          if (sid && !seen.has(sid)) { seen.add(sid); const s = a.subject; acc.push({ title: s ? `${s.name}${s.code ? ' (' + s.code + ')' : ''}` : 'none', value: sid }); }
          return acc;
        }, []);
    }
  }

  registerModalDropdown(event: { depended: number; ref: any }) {
    this.modalDropdownRegistry[event.depended] = event.ref;
  }

  onModalDropdownChange(event: { value: any, depended: number }) {
    if (event.depended == 1) {
      this.filteredStudentList = [];
      this.form.patchValue({ academic_year_id: null, exam_id: null, subject_id: null, student_id: null }, { emitEvent: false });
    }
    if (event.depended == 2) {
      this.filteredStudentList = [];
      this.form.patchValue({ exam_id: null, subject_id: null, student_id: null }, { emitEvent: false });
    }
    if (event.depended == 3) {
      this.filteredStudentList = [];
      this.form.patchValue({ subject_id: null, student_id: null }, { emitEvent: false });
    }
    if (event.depended == 4) {
      this.filteredStudentList = [];
      this.form.patchValue({ student_id: null }, { emitEvent: false });
      const subject_id = event.value?.value || event.value;
      const exam_id = this.form.get('exam_id')?.value;
      if (subject_id && exam_id) {
        this.ds.get_students_by_subject(subject_id, exam_id).then((list: any[]) => {
          this.filteredStudentList = list;
        });
      }
    }
    this.update_modal_filtered_options();
    const current = event.depended;
    for (const key of Object.keys(this.modalDropdownRegistry).map(Number)) {
      if (key > current) {
        this.modalDropdownRegistry[key]?.reset();
      }
    }
  }

  /**
   * Cascading option lists for a University → Academic Year → Exam → Subject selection, derived
   * from the assessments so only combinations that actually exist can be picked. Each level stays
   * empty until the one above it is chosen — the add form and the bulk form both require all four.
   */
  build_cascade_options(uni: any, ay: any, exam: any) {
    const data = this.ds.dropdown_list?.assessment_data_list;
    const list = this.ds.dropdown_list;

    let academic_years: any[] = [];
    if (uni && data) {
      const ids = [...new Set(data.filter((a: any) => a.university_id == uni).map((a: any) => a.academic_year_id))];
      academic_years = list?.academic_list?.filter((a: any) => ids.includes(a.id)) || [];
    }

    let exams: any[] = [];
    if (uni && ay && data) {
      const ids = [...new Set(data.filter((a: any) => a.university_id == uni && a.academic_year_id == ay).map((a: any) => a.exam_id))];
      exams = (list?.exam_list?.filter((e: any) => ids.includes(e.id)) || []).map((e: any) => ({ title: `${e.name}${e.code ? ' (' + e.code + ')' : ''}`, value: e.id }));
    }

    let subjects: any[] = [];
    if (uni && ay && exam && data) {
      const seen = new Set();
      subjects = data
        .filter((a: any) => a.university_id == uni && a.academic_year_id == ay && a.exam_id == exam)
        .reduce((acc: any[], a: any) => {
          const sid = a.subject?.id || a.subject_id;
          if (sid && !seen.has(sid)) { seen.add(sid); const s = a.subject; acc.push({ title: s ? `${s.name}${s.code ? ' (' + s.code + ')' : ''}` : 'none', value: sid }); }
          return acc;
        }, []);
    }

    return { academic_years, exams, subjects };
  }

  update_modal_filtered_options() {
    const options = this.build_cascade_options(
      this.form.get('university_id')?.value,
      this.form.get('academic_year_id')?.value,
      this.form.get('exam_id')?.value
    );
    this.modal_filter_academic_years = options.academic_years;
    this.modal_filter_exams = options.exams;
    this.modal_filter_subjects = options.subjects;
  }


  country_cols: any = [
    { field: 'resolved_roll_number', headerName: 'Roll No.', filter: true, flex: 1 },
    { field: 'barcode', headerName: 'Bar-Code', filter: true, flex: 1, cellStyle: (params: any) => params.value === 'Absent' ? { color: 'red', fontWeight: '600' } : {} },
    { headerName: "", sortable: false, filter: false, cellRenderer: ListActionButtonsComponent, headerClass: 'custom-header-center', cellRendererParams: { actions: this.actions.bind(this) }, editable: false, pinned: 'right', width: 130, minWidth: 130, maxWidth: 130 }
  ];

  // University / Academic Year / Exam / Subject are picked once in the bulk modal's own form and
  // merged into every row via bulk_extra_data, so the CSV only carries what varies per row.
  // Roll No resolves against bulk_student_roll_list — the students mapped to the chosen exam +
  // subject — rather than every roll number in the system, which the same number can repeat across.
  bulk_columns = [
    { column_name: 'Roll No', type: 'link', linked_list: 'bulk_student_roll_list', returning_key: 'id', return_as: 'student_id', sample_value: '2021001' },
    { column_name: 'Barcode', type: 'text', return_as: 'barcode', sample_value: 'BC12345' },
    // { column_name: 'Status', type: 'text', return_as: 'is_active', sample_value: true },
  ];

  bulk_filter_academic_years: any[] = [];
  bulk_filter_exams: any[] = [];
  bulk_filter_subjects: any[] = [];

  get bulk_extra_data() {
    return this.bulk_form.value;
  }

  onBulkDropdownChange(depended: number) {
    if (depended == 1) this.bulk_form.patchValue({ academic_year_id: '', exam_id: '', subject_id: '' }, { emitEvent: false });
    if (depended == 2) this.bulk_form.patchValue({ exam_id: '', subject_id: '' }, { emitEvent: false });
    if (depended == 3) this.bulk_form.patchValue({ subject_id: '' }, { emitEvent: false });

    const { university_id, academic_year_id, exam_id } = this.bulk_form.value;
    const options = this.build_cascade_options(university_id, academic_year_id, exam_id);
    this.bulk_filter_academic_years = options.academic_years;
    this.bulk_filter_exams = options.exams;
    this.bulk_filter_subjects = options.subjects;

    this.load_bulk_student_roll_list();
  }

  /**
   * Narrow the roll numbers the CSV may reference to the chosen exam + subject. Written onto the
   * shared dropdown_list because that is where BulkUploadComponent resolves a column's linked_list.
   */
  async load_bulk_student_roll_list() {
    const { exam_id, subject_id } = this.bulk_form.value;
    if (!exam_id || !subject_id) {
      this.ds.dropdown_list.bulk_student_roll_list = [];
      return;
    }
    this.ds.dropdown_list.bulk_student_roll_list = await this.ds.get_students_by_subject(subject_id, exam_id);
  }

  open_bulk_modal() {
    this.bulk_form.reset({ university_id: '', academic_year_id: '', exam_id: '', subject_id: '' });
    this.bulk_filter_academic_years = [];
    this.bulk_filter_exams = [];
    this.bulk_filter_subjects = [];
    this.ds.dropdown_list.bulk_student_roll_list = [];
    this.bulk_modal?.open();
  }

  bulk_columns_download = [
    { column_name: 'Exam', download_bind: 'exam_id', type: 'link', linked_list: 'exam_list', linked_from: 'exam', return_as: 'exam_id', sample_value: 'AD' },
    { column_name: 'Subject', download_bind: 'subject_id', type: 'link', linked_list: 'subject_list', linked_from: 'subject', return_as: 'subject_id', sample_value: 'AD' },
    { column_name: 'Roll No', download_bind: 'student_id', type: 'link', linked_list: 'student_list', linked_from: 'student', return_as: 'student_id', sample_value: 'AD' },
    { column_name: 'Barcode', type: 'text', return_as: 'barcode', sample_value: '111' },
  ]

  actions(row: any) {
    let actions: any = []
    // actions.push({ text: row.is_active ? 'Disable' : 'Enable', shortcut: 'u', color: 'gray', icon: row.is_active ? 'bx bx-block' : 'bx bx-check-circle', function: this.disable.bind(this) })
    actions.push({ text: 'View', shortcut: 'u', color: 'sky', icon: 'bx bx-eye', function: this.open_view_modal.bind(this), permissions: this.gs.user_role_data?.script_management_barcode_mapping_view })
    // Once a script has been added using this barcode, the mapping it came from can no longer be edited or deleted.
    actions.push({ text: 'Edit', shortcut: 'u', color: 'sky', icon: 'bx bx-edit', disabled: row.is_active === false || row.has_script, function: this.open_form_modal.bind(this), permissions: this.gs.user_role_data?.script_management_barcode_mapping_update })
    actions.push({ text: 'Delete', shortcut: 'd', color: 'red', icon: 'bx bx-trash', disabled: row.has_script, function: this.delete.bind(this), permissions: this.gs.user_role_data?.script_management_barcode_mapping_delete })
    return actions
  }
  constructor(public gs: GlobalService, public route: Router, public fb: FormBuilder, public bcs: BarcodeService, public ar: ActivatedRoute, public ds: DropdownService, public vs: ValidationService) {
    this.form = this.fb.group({
      university_id: ['', [Validators.required]],
      academic_year_id: ['', [Validators.required]],
      exam_id: ['', [Validators.required]],
      subject_id: ['', [Validators.required]],
      student_id: ['', [Validators.required]],
      // name: ['', [Validators.required]],
      barcode: ['', [Validators.pattern(this.vs.name_pattern)]],
      is_active: [true]
    });
    this.bulk_form = this.fb.group({
      university_id: ['', [Validators.required]],
      academic_year_id: ['', [Validators.required]],
      exam_id: ['', [Validators.required]],
      subject_id: ['', [Validators.required]],
    });
  }

  async ngOnInit() {
    // Access Level: lock the filters this user cannot change before the first fetch.
    this.gs.apply_access_scope_to_params(this.params);
    // 'assessment' fills assessment_list (used by patch_latest_assessment) and 'assessment_data'
    // fills assessment_data_list (used by the filter/modal option builders). 'assessment_list' is
    // not a valid filter key — get_dropdown_list matches keys exactly — so it loaded nothing.
    this.ds?.clear_dropdown_list();
    await this.ds?.get_dropdown_list(['academic-year', 'university', 'exam', 'subject', 'student', 'student_data', 'assessment_data', 'assess', 'assessment']);
    this.patch_latest_assessment();
    this.update_list_filtered_options();
    setTimeout(() => {
      this.filterDropdownRegistry[1]?.writeValue(this.params.university_id);
      this.filterDropdownRegistry[2]?.writeValue(this.params.academic_year_id);
      this.filterDropdownRegistry[3]?.writeValue(this.params.exam_id);
      this.filterDropdownRegistry[4]?.writeValue(this.params.subject_id);
    });
    await this.get_list(this.params);
  }
  async get_list(filter?: any) {
    try {
      await this.ds.get_dropdown_list(['student']);
      const res = await this.bcs.get_list({ ...this.params, ...filter });
      const page = filter?.page || 1;
      const pagesize = filter?.page_size || 10;
      const startindex = (page - 1) * pagesize;

      res.data = res.data.map((item: any, index: number) => {
        const student = (this.ds.dropdown_list?.student_list || []).find((s: any) => s.id == item.student_id);
        const mapping = (student?.StudentMappings || []).find((m: any) =>
          m.exam_id == item.exam_id && Array.isArray(m.subject_ids) && m.subject_ids.includes(+item.subject_id)
        );
        return {
          ...item,
          resolved_roll_number: mapping?.roll_number || '—'
        };
      });

      this.items = res;
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }
  async delete(item: any, index: any) {
    this.selected_item = item;
    this.confirmation_delete_modal?.open();
  }
  async disable(item: any, index: any) {
    this.selected_item = item;
    this.confirmation_disabled_modal?.open();
  }
  async update_country(item: any) {
    try {
      let form_value = this.form.value;
      let data = item == true ? form_value.is_active == false : form_value.is_active == true
      let update = await this.bcs.update(this.selected_item.id, { is_active: data });
      this.gs.toastr_shows_function('Status has been successfully updated', 'Success', 'success');
      this.confirmation_delete_modal?.close()
      await this.get_list(this.params);
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }
  async disable_country() {
    try {
      let res = await this.bcs.delete(this.selected_item.id);
      this.gs.toastr_shows_function(res?.message, 'Success', 'success');
      this.selected_item = {};
      this.confirmation_delete_modal?.close()
      await this.get_list(this.params);
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }
  async bulk_delete(ids: any[]) {
    try {
      let res: any
      for (let id of ids) {
        res = await this.bcs.delete(id);
      }
      this.gs.toastr_shows_function(res?.message, 'Success', 'success');
      await this.get_list(this.params);
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }
  async open_form_modal(item?: any) {
    this.selected_item = item || {};
    if (this.selected_item && this.selected_item.id) {
      let response = await this.bcs.get(item.id);
      this.selected_item = { ...response.data };
      this.form.reset();
      this.form.patchValue({ university_id: this.selected_item.university_id, barcode: this.selected_item.barcode === 'Absent' ? '' : this.selected_item.barcode });
      this.update_modal_filtered_options();
      setTimeout(() => {
        this.form.patchValue({ academic_year_id: this.selected_item.academic_year_id });
        this.update_modal_filtered_options();
        setTimeout(() => {
          this.form.patchValue({ exam_id: this.selected_item.exam_id });
          this.update_modal_filtered_options();
          setTimeout(() => {
            this.form.patchValue({ subject_id: this.selected_item.subject_id });
            this.ds.get_students_by_subject(this.selected_item.subject_id, this.selected_item.exam_id).then((list: any[]) => {
              this.filteredStudentList = list;
              setTimeout(() => {
                this.form.patchValue({ student_id: this.selected_item.student_id });
              });
            });
          });
        });
      });
    } else {
      this.selected_item = {};
      this.form.reset();
      this.filteredStudentList = [];
    }
    this.form_modal?.open();
  }
  get_roll_number(item: any): string {
    const student = (this.ds.dropdown_list?.student_list || []).find((s: any) => s.id == item?.student_id);
    const mapping = (student?.StudentMappings || []).find((m: any) =>
      m.exam_id == item?.exam_id && Array.isArray(m.subject_ids) && m.subject_ids.includes(+item?.subject_id)
    );
    return mapping?.roll_number || '';
  }

  open_view_modal(item: any) {
    console.log(item);
    this.selected_item = item || {};
    this.view_modal?.open();
  }
  close_form_modal(): void {
    this.form_modal?.close();
  }
  async submit_country() {
    try {
      if (this.form.invalid) {
        this.gs.toastr_shows_function('Please fill all required fields correctly.', 'Error', 'error');
        this.form.markAllAsTouched();
        return;
      }
      let form_value = this.form.value;
      form_value.is_active = true;
      if (this.selected_item && this.selected_item.id) {
        let update_response = await this.bcs.update(this.selected_item.id, form_value);
        this.gs.toastr_shows_function('Changes updated successfully', 'Success', 'success');
      } else {
        let create_response = await this.bcs.add(form_value);
        this.gs.toastr_shows_function(create_response?.message, 'Success', 'success');
      }
      await this.get_list(this.params);
      this.close_form_modal();
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }

  async bulk_upload(data: any, spinner: any) {
    try {
      const updatedData = data.map((item: any) => ({
        ...item,
        barcode: String(item.barcode),
        is_active: true
      }));
      console.log(updatedData, 'dataa');

      await this.gs.bulk_form_submission(updatedData, this.bcs, this.params, this.get_list.bind(this), this.bulk_modal, spinner)
    } catch (error: any) {
      console.error(error?.error?.message, '');
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }
}
